import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, formatApiError, formatRupiah, API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Trash, FloppyDisk, Eye, Image as ImageIcon, ArrowUp, ArrowDown
} from "@phosphor-icons/react";
import {
  useFieldsBuilder, FieldsDndProvider, FieldsPalette, FieldsCanvas, RoutingRulesEditor,
} from "@/components/fieldsBuilder";

function genId() { return Math.random().toString(36).slice(2, 10); }

const MENU_SCRIPT = `function doPost(e) {
  const sheet = SpreadsheetApp.getActiveSpreadsheet().getActiveSheet();
  const data = JSON.parse(e.postData.contents);
  const headers = ['Waktu', 'Menu', 'Meja', 'Nama', 'Items', 'Total', 'Catatan'];
  if (sheet.getLastRow() === 0) sheet.appendRow(headers);
  sheet.appendRow([
    data.submitted_at, data.menu_name, data.table_number, data.customer_name,
    (data.items || []).join(', '), data.total, data.notes
  ]);
  return ContentService.createTextOutput(JSON.stringify({ok:true})).setMimeType(ContentService.MimeType.JSON);
}`;

export default function MenuBuilder() {
  const { id } = useParams();
  const isEdit = !!id;
  const nav = useNavigate();
  const { user } = useAuth();
  const fileInputs = useRef({});

  const [menu, setMenu] = useState({
    name: "",
    description: "",
    whatsapp_number: user?.whatsapp_number || "",
    intro_message: "Halo, saya ingin memesan:",
    require_table: true,
    currency: "Rp",
    categories: ["Makanan", "Minuman"],
    items: [],
    sheets_webhook_url: "",
    fields: [],
    routing_rules: [],
    active: true,
    slug: "",
  });
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [newCat, setNewCat] = useState("");

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/menus/${id}`)
      .then(r => setMenu(r.data))
      .catch(e => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const addItem = () => {
    const cat = menu.categories[0] || "Menu";
    setMenu(m => ({
      ...m,
      items: [...m.items, { id: genId(), name: "Menu baru", description: "", price: 0, category: cat, available: true, image_path: null }],
    }));
  };

  const updateItem = (idx, patch) => {
    setMenu(m => {
      const copy = [...m.items];
      copy[idx] = { ...copy[idx], ...patch };
      return { ...m, items: copy };
    });
  };

  const removeItem = (idx) => {
    setMenu(m => ({ ...m, items: m.items.filter((_, i) => i !== idx) }));
  };

  const moveItem = (idx, dir) => {
    setMenu(m => {
      const copy = [...m.items];
      const j = idx + dir;
      if (j < 0 || j >= copy.length) return m;
      [copy[idx], copy[j]] = [copy[j], copy[idx]];
      return { ...m, items: copy };
    });
  };

  const addCategory = () => {
    const v = newCat.trim();
    if (!v) return;
    if (menu.categories.includes(v)) return toast.error("Kategori sudah ada");
    setMenu(m => ({ ...m, categories: [...m.categories, v] }));
    setNewCat("");
  };

  const removeCategory = (cat) => {
    setMenu(m => ({
      ...m,
      categories: m.categories.filter(c => c !== cat),
      items: m.items.map(it => it.category === cat ? { ...it, category: m.categories[0] || "Menu" } : it),
    }));
  };

  const uploadImage = async (idx, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      updateItem(idx, { image_path: data.path });
      toast.success("Foto diunggah");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gagal upload");
    }
  };

  const save = async () => {
    if (!menu.name.trim()) return toast.error("Nama menu wajib diisi");
    if (!menu.whatsapp_number.trim()) return toast.error("Nomor WhatsApp wajib diisi");
    setSaving(true);
    try {
      const payload = {
        name: menu.name,
        description: menu.description,
        whatsapp_number: menu.whatsapp_number,
        intro_message: menu.intro_message,
        require_table: menu.require_table,
        currency: menu.currency,
        categories: menu.categories,
        items: menu.items,
        sheets_webhook_url: menu.sheets_webhook_url || "",
        fields: menu.fields || [],
        routing_rules: menu.routing_rules || [],
      };
      if (isEdit) {
        payload.active = menu.active;
        const { data } = await api.patch(`/menus/${id}`, payload);
        setMenu(data);
        toast.success("Tersimpan!");
      } else {
        const { data } = await api.post(`/menus`, payload);
        toast.success("Menu dibuat!");
        nav(`/menus/${data.id}`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-[#FDFBF7]"><Navbar /><p className="p-12 font-display text-2xl">Memuat…</p></div>;
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex flex-wrap justify-between items-center gap-3">
          <Link to="/dashboard" className="inline-flex items-center gap-2 font-bold" data-testid="menu-back-link">
            <ArrowLeft size={18} weight="bold" /> Kembali
          </Link>
          <div className="flex gap-2 flex-wrap">
            {isEdit && menu.slug && (
              <a href={`/m/${menu.slug}`} target="_blank" rel="noreferrer" className="nb-btn" data-testid="menu-preview-link">
                <Eye size={18} weight="bold" /> Pratinjau
              </a>
            )}
            <button onClick={save} disabled={saving} className="nb-btn nb-btn-secondary" data-testid="menu-save-button">
              <FloppyDisk size={18} weight="bold" /> {saving ? "Menyimpan…" : "Simpan"}
            </button>
          </div>
        </div>

        <div className="mt-6">
          <span className="nb-badge" style={{ background: "#FFD166" }}>{isEdit ? "Edit Menu" : "Menu Baru"}</span>
          <h1 className="font-display font-black text-4xl tracking-tight mt-3">{isEdit ? menu.name : "Buat menu Dine-in"}</h1>
        </div>

        <div className="grid lg:grid-cols-12 gap-6 mt-8">
          {/* Settings */}
          <div className="lg:col-span-5 nb-card p-6 space-y-4 self-start">
            <h2 className="font-display font-bold text-xl">Pengaturan</h2>
            <div>
              <label className="nb-label">Nama Restoran / Menu</label>
              <input className="nb-input" value={menu.name} onChange={e => setMenu({ ...menu, name: e.target.value })} placeholder="Warung Bu Ani" data-testid="menu-name-input" />
            </div>
            <div>
              <label className="nb-label">Deskripsi</label>
              <textarea className="nb-input" rows={2} value={menu.description} onChange={e => setMenu({ ...menu, description: e.target.value })} placeholder="Masakan rumahan, halal, bersih" data-testid="menu-desc-input" />
            </div>
            <div>
              <label className="nb-label">Nomor WhatsApp Tujuan</label>
              <input className="nb-input" value={menu.whatsapp_number} onChange={e => setMenu({ ...menu, whatsapp_number: e.target.value })} placeholder="628123456789" data-testid="menu-wa-input" />
            </div>
            <div>
              <label className="nb-label">Pesan Pembuka di WhatsApp</label>
              <textarea className="nb-input" rows={2} value={menu.intro_message} onChange={e => setMenu({ ...menu, intro_message: e.target.value })} data-testid="menu-intro-input" />
            </div>
            <div className="flex items-center gap-3">
              <input type="checkbox" id="req-table" checked={!!menu.require_table} onChange={e => setMenu({ ...menu, require_table: e.target.checked })} className="w-5 h-5 border-2 border-[#121212]" data-testid="menu-require-table-toggle" />
              <label htmlFor="req-table" className="font-bold">Wajibkan nomor meja (Dine-in)</label>
            </div>
            {isEdit && (
              <div className="flex items-center gap-3">
                <input type="checkbox" id="menu-active" checked={!!menu.active} onChange={e => setMenu({ ...menu, active: e.target.checked })} className="w-5 h-5 border-2 border-[#121212]" data-testid="menu-active-toggle" />
                <label htmlFor="menu-active" className="font-bold">Menu aktif</label>
              </div>
            )}

            <div className="pt-3 border-t-2 border-[#121212]">
              <label className="nb-label">Kategori Menu</label>
              <div className="flex flex-wrap gap-2 mb-2">
                {menu.categories.map(c => (
                  <span key={c} className="nb-badge flex items-center gap-2 bg-[#A7E4C0]">
                    {c}
                    <button onClick={() => removeCategory(c)} className="font-black hover:text-red-700" data-testid={`cat-remove-${c}`}>×</button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input className="nb-input" value={newCat} onChange={e => setNewCat(e.target.value)} placeholder="Tambah kategori" data-testid="menu-new-cat-input" />
                <button onClick={addCategory} className="nb-btn" data-testid="menu-add-cat-button"><Plus size={16} weight="bold" /></button>
              </div>
            </div>
          </div>

          {/* Combined Builder: Daftar Menu + Pertanyaan Pelanggan */}
          <div className="lg:col-span-7 space-y-4">
            <MenuBuilderTabs
              menu={menu}
              setMenu={setMenu}
              addItem={addItem}
              updateItem={updateItem}
              removeItem={removeItem}
              moveItem={moveItem}
              uploadImage={uploadImage}
              fileInputs={fileInputs}
            />

            <div className="nb-card p-5 bg-[#A7E4C0]/30" data-testid="sheets-webhook-card">
              <span className="nb-badge bg-[#A7E4C0]">Google Sheets</span>
              <h3 className="font-display font-bold text-lg mt-2">Catat pesanan ke Spreadsheet</h3>
              <p className="text-xs text-[#4A4A4A] mt-1">Semua pesanan dine-in otomatis tercatat di Google Sheet Anda.</p>
              <div className="mt-3">
                <label className="nb-label">URL Web App Apps Script</label>
                <input className="nb-input" value={menu.sheets_webhook_url || ""} onChange={e => setMenu({ ...menu, sheets_webhook_url: e.target.value })} placeholder="https://script.google.com/macros/s/.../exec" data-testid="sheets-webhook-url-input" />
              </div>
              <details className="mt-3">
                <summary className="cursor-pointer font-bold text-sm" data-testid="sheets-help-toggle">Lihat panduan setup</summary>
                <div className="mt-2 space-y-2 text-sm">
                  <ol className="list-decimal pl-5 space-y-1">
                    <li>Buka Google Sheets baru. Menu <strong>Extensions → Apps Script</strong>.</li>
                    <li>Hapus kode default, salin & tempel kode di bawah.</li>
                    <li>Klik <strong>Deploy → New deployment → Web app</strong>. Akses: <strong>Anyone</strong>.</li>
                    <li>Copy URL Web App, tempel di kolom di atas.</li>
                  </ol>
                  <pre className="mono text-xs bg-[#121212] text-[#FDFBF7] p-3 rounded-lg overflow-x-auto border-2 border-[#121212]" data-testid="sheets-script-code">{MENU_SCRIPT}</pre>
                  <button type="button" onClick={() => { navigator.clipboard.writeText(MENU_SCRIPT); toast.success("Script disalin"); }} className="nb-btn" data-testid="sheets-copy-script-button">
                    Salin Script
                  </button>
                </div>
              </details>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function MenuBuilderTabs({ menu, setMenu, addItem, updateItem, removeItem, moveItem, uploadImage, fileInputs }) {
  const [tab, setTab] = useState("items");
  return (
    <div className="nb-card overflow-hidden">
      <div className="flex border-b-2 border-[#121212]">
        <button
          type="button"
          onClick={() => setTab("items")}
          className={`flex-1 px-4 py-3 font-display font-bold text-sm border-r-2 border-[#121212] ${tab === "items" ? "bg-[#FFD166]" : "bg-white hover:bg-[#FDFBF7]"}`}
          data-testid="menu-tab-items"
        >
          🍽️ Daftar Menu <span className="mono text-xs opacity-60">({menu.items?.length || 0})</span>
        </button>
        <button
          type="button"
          onClick={() => setTab("fields")}
          className={`flex-1 px-4 py-3 font-display font-bold text-sm ${tab === "fields" ? "bg-[#CDB4DB]" : "bg-white hover:bg-[#FDFBF7]"}`}
          data-testid="menu-tab-fields"
        >
          📝 Pertanyaan & Routing <span className="mono text-xs opacity-60">({(menu.fields?.length || 0) + (menu.routing_rules?.length ? ` · ${menu.routing_rules.length} rule` : "")})</span>
        </button>
      </div>

      <div className="p-5 space-y-4">
        {tab === "items" ? (
          <ItemsPanel
            menu={menu}
            addItem={addItem}
            updateItem={updateItem}
            removeItem={removeItem}
            moveItem={moveItem}
            uploadImage={uploadImage}
            fileInputs={fileInputs}
          />
        ) : (
          <FieldsBuilderSection menu={menu} setMenu={setMenu} />
        )}
      </div>
    </div>
  );
}

function ItemsPanel({ menu, addItem, updateItem, removeItem, moveItem, uploadImage, fileInputs }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display font-bold text-xl">Item Menu</h2>
          <p className="text-xs text-[#4A4A4A]">Daftar menu yang dipilih pelanggan (nama, harga, foto).</p>
        </div>
        <button onClick={addItem} className="nb-btn nb-btn-secondary" data-testid="menu-add-item-button">
          <Plus size={18} weight="bold" /> Tambah Item
        </button>
      </div>

      {menu.items.length === 0 && (
        <div className="border-2 border-dashed border-[#121212] rounded-lg p-8 text-center">
          <p className="text-[#4A4A4A]">Belum ada item. Klik <strong>Tambah Item</strong> untuk mulai.</p>
        </div>
      )}

      {menu.items.map((it, idx) => (
        <div key={it.id} className="border-2 border-[#121212] rounded-lg p-4 bg-white" data-testid={`item-card-${idx}`}>
          <div className="flex gap-4">
            <div className="w-24 h-24 shrink-0 border-2 border-[#121212] rounded-lg overflow-hidden bg-[#FDFBF7] flex items-center justify-center relative">
              {it.image_path ? (
                <img src={`${API}/files/${it.image_path}`} alt={it.name} className="w-full h-full object-cover" />
              ) : (
                <ImageIcon size={28} weight="bold" className="text-[#4A4A4A]" />
              )}
              <input
                ref={el => fileInputs.current[idx] = el}
                type="file" accept="image/*"
                className="hidden"
                onChange={e => uploadImage(idx, e.target.files?.[0])}
                data-testid={`item-image-input-${idx}`}
              />
            </div>
            <div className="flex-1 space-y-2">
              <div className="grid sm:grid-cols-12 gap-2">
                <div className="sm:col-span-7">
                  <label className="nb-label">Nama Menu</label>
                  <input className="nb-input" value={it.name} onChange={e => updateItem(idx, { name: e.target.value })} data-testid={`item-name-${idx}`} />
                </div>
                <div className="sm:col-span-5">
                  <label className="nb-label">Harga (Rp)</label>
                  <input type="number" className="nb-input" value={it.price} onChange={e => updateItem(idx, { price: Number(e.target.value) || 0 })} data-testid={`item-price-${idx}`} />
                </div>
              </div>
              <div className="grid sm:grid-cols-12 gap-2">
                <div className="sm:col-span-8">
                  <label className="nb-label">Deskripsi</label>
                  <input className="nb-input" value={it.description || ""} onChange={e => updateItem(idx, { description: e.target.value })} placeholder="Singkat & menggoda" data-testid={`item-desc-${idx}`} />
                </div>
                <div className="sm:col-span-4">
                  <label className="nb-label">Kategori</label>
                  <select className="nb-input" value={it.category} onChange={e => updateItem(idx, { category: e.target.value })} data-testid={`item-cat-${idx}`}>
                    {menu.categories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex justify-between items-center pt-2 flex-wrap gap-2">
                <div className="flex items-center gap-4 text-sm font-bold">
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={!!it.available} onChange={e => updateItem(idx, { available: e.target.checked })} className="w-4 h-4 border-2 border-[#121212]" data-testid={`item-avail-${idx}`} /> Tersedia
                  </label>
                  <button onClick={() => fileInputs.current[idx]?.click()} className="nb-btn !py-1 !px-3 text-xs" data-testid={`item-upload-${idx}`}>
                    <ImageIcon size={14} weight="bold" /> {it.image_path ? "Ganti Foto" : "Upload Foto"}
                  </button>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => moveItem(idx, -1)} className="nb-btn" data-testid={`item-up-${idx}`}><ArrowUp size={14} weight="bold" /></button>
                  <button onClick={() => moveItem(idx, 1)} className="nb-btn" data-testid={`item-down-${idx}`}><ArrowDown size={14} weight="bold" /></button>
                  <button onClick={() => removeItem(idx)} className="nb-btn" data-testid={`item-remove-${idx}`}><Trash size={14} weight="bold" /></button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function FieldsBuilderSection({ menu, setMenu }) {
  const fb = useFieldsBuilder(menu, setMenu);
  return (
    <div>
      <div>
        <h2 className="font-display font-bold text-xl">Pertanyaan Tambahan</h2>
        <p className="text-xs text-[#4A4A4A] mt-1">
          Tambahkan pertanyaan opsional (metode bayar, alergi, alamat) yang muncul saat pelanggan checkout. Atur juga routing WA tim di bawah.
        </p>
      </div>
      <FieldsDndProvider onDragEnd={fb.handleDragEnd}>
        <div className="grid sm:grid-cols-12 gap-4 mt-4">
          <div className="sm:col-span-5">
            <FieldsPalette onAdd={fb.addFieldByType} title="Palet Elemen" subtitle="Seret atau klik" />
          </div>
          <div className="sm:col-span-7 space-y-4">
            <FieldsCanvas
              fields={fb.fields}
              updateField={fb.updateField}
              removeField={fb.removeField}
              moveField={fb.moveField}
              emptyText="Belum ada pertanyaan tambahan"
            />
            <RoutingRulesEditor
              fields={fb.fields}
              rules={fb.routing_rules}
              onChange={fb.setRoutingRules}
            />
          </div>
        </div>
      </FieldsDndProvider>
    </div>
  );
}
